import _ from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';

import NoAppointments from './NoAppointments';
import AppointmentView from './AppointmentView';
import { PracticeOnly, ProviderAndPractice } from './common';

import * as api from '../../../api';

import styles from '../index.module.css';
import ProviderInfo from './ProviderView/ProviderInfo';
import PracticeInfo from './PracticeView/PracticeInfo';

import { SchedulingContext } from '../../../scenes/Form/ScheduleAppointmentPage/SchedulingContext';
import AlternateProviderView from './AlternateProviderView';

const MultiView: React.FC = () => {
  const {
    patientOrder,
    mainProviderUserId,
    availableEhrTimeSlots,
    practices,
    providers,
    fieldConfig,
    patientPcpPreferred,
    onAppointmentSelected,
  } = useContext(SchedulingContext);
  const { scheduleAppointmentDisplayBy } = fieldConfig;

  const [uniqueConfigIds, setUniqueConfigIds] = useState<
    Pick<api.EhrTimeSlot.EhrTimeSlot, 'practiceId' | 'providerId'>[]
  >([]);
  const [mainProviderHasSlots, setMainProviderHasSlots] =
    useState<boolean>(true);
  const [mainPracticeId, setMainPracticeId] = useState<string>('');
  const mainProviderId =
    (patientOrder?.referredToProvider?.id || mainProviderUserId) ?? null;
  useEffect(() => {
    const uniqueSlotConfigs = _.uniqBy(availableEhrTimeSlots, (slot) => {
      return JSON.stringify(_.pick(slot, ['providerId', 'practiceId']));
    });
    if (mainProviderId) {
      setMainProviderHasSlots(
        uniqueSlotConfigs
          .map((slot) => slot.providerId)
          .includes(mainProviderId),
      );
      setMainPracticeId(uniqueSlotConfigs[0].practiceId);
    }
    setUniqueConfigIds(
      uniqueSlotConfigs.map((slot) =>
        _.pick(slot, ['providerId', 'practiceId']),
      ),
    );
  }, [availableEhrTimeSlots, practices, mainProviderId]);
  const providerAndPractice = useMemo(():
    | ProviderAndPractice[]
    | PracticeOnly[] => {
    const providerAndPractices: ProviderAndPractice[] | PracticeOnly[] =
      uniqueConfigIds.map((tuple) => {
        const { providerId, practiceId } = tuple;
        const slot = availableEhrTimeSlots.find(
          (slot) =>
            slot.providerId === providerId && slot.practiceId === practiceId,
        )!;
        const practice = practices[slot.practiceId];
        const provider = slot.providerId
          ? providers[slot.providerId]
          : undefined;
        if (provider) {
          const result: ProviderAndPractice = {
            ...slot,
            ...provider,
            ...practice,
            practiceId: slot.practiceId,
            providerUserId: slot.providerId!,
            specialties: provider.specialties ?? null,
          };
          return result;
        }
        const result: PracticeOnly = {
          ...slot,
          ...practice,
        };
        return result;
      });
    return scheduleAppointmentDisplayBy ===
      api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice
      ? _.uniqBy(providerAndPractices, 'practiceId')
      : providerAndPractices;
  }, [
    practices,
    providers,
    scheduleAppointmentDisplayBy,
    uniqueConfigIds,
    availableEhrTimeSlots,
  ]);

  if (providerAndPractice.length === 0) {
    return (
      <NoAppointments
        className={styles.MultiProviderNoAppointmentContent}
        fieldConfig={fieldConfig}
      />
    );
  }

  return !patientOrder?.alternateReferredToProvider && !patientPcpPreferred ? (
    <div className={styles.MultiProviderContainer}>
      {providerAndPractice.map((config) => {
        const distance = availableEhrTimeSlots.find(
          (slot) => slot.practiceId === config.practiceId,
        )?.distance;
        return (
          <div
            key={`${config.practiceId}${
              'providerUserId' in config ? `,${config.providerUserId}` : ''
            }`}
          >
            <div className={styles.MultiProviderListItemDivider}></div>
            <div className={styles.MultiProviderListItem}>
              <div className={styles.MultiProviderItem}>
                {'providerUserId' in config &&
                scheduleAppointmentDisplayBy ===
                  api.PrevisitFormTemplateFieldConfig
                    .ScheduleAppointmentDisplayBy.Provider ? (
                  <ProviderInfo config={config} distance={distance} />
                ) : (
                  <PracticeInfo config={config} distance={distance} />
                )}
                <div className={styles.MultiProviderAppointmentContainer}>
                  <AppointmentView
                    timeSlots={availableEhrTimeSlots}
                    config={config}
                    onAppointmentSelected={onAppointmentSelected}
                    fieldConfig={fieldConfig}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <AlternateProviderView
      mainProviderHasSlots={mainProviderHasSlots}
      providerAndPractice={providerAndPractice}
      mainProviderId={mainProviderId}
      mainPracticeId={mainPracticeId}
    />
  );
};

export default MultiView;
